import React, { useEffect, useMemo, useState } from "react";
import CopilotQualifyProgress from "./CopilotQualifyProgress";
import PrecedentBlock from "../../../components/PrecedentBlock/PrecedentBlock";

/* The bid / no-bid card.
 *
 * Rebuilt around two problems in the first version.
 *
 * ACCURACY. It printed each aspect's configured weight (7%, 9%, 6%…) next to a
 * readiness bar. Those weights do not sum to 100 — the backend scores only the
 * aspects that actually matched requirements and normalises over THAT total. So
 * a "7% weight" aspect was really deciding 14.9% of the score, and the reader
 * had no way to know. Worse, that 14.9% rested on a single requirement while a
 * 19.1% aspect rested on sixty-five. Everything here is now expressed as points
 * out of the points available, which is the number that actually moves the 40.
 *
 * INFORMATION. The backend scores every requirement 0-5 for evidence quality
 * and the card ignored all of it, showing only aggregates. That distribution is
 * the most decision-useful thing in the payload: "39 requirements have no
 * evidence at all" tells a bid manager more than any percentage.
 */

const VERDICT = {
  strong: {
    label: "Bid",
    color: "#1F7D38",
    soft: "rgba(45,138,78,0.10)",
    ring: "rgba(45,138,78,0.25)",
    line: "Your knowledge base can carry this one.",
  },
  caution: {
    label: "Bid with caution",
    color: "#92620B",
    soft: "rgba(217,119,6,0.10)",
    ring: "rgba(217,119,6,0.25)",
    line: "Winnable, but it will take real effort.",
  },
  needs_improvement: {
    label: "Don't bid",
    color: "#DC2626",
    soft: "rgba(220,38,38,0.08)",
    ring: "rgba(220,38,38,0.22)",
    line: "The evidence isn't there yet.",
  },
};

const verdictOf = (v) => VERDICT[v] || VERDICT.caution;

/* A bullet in the fitment section — same shape as the score's own bullets
 * would use, kept separate because the two describe different data. */
const SummaryLine = ({ children }) => (
  <li className="flex gap-2.5 py-[3px]">
    <span className="mt-[9px] h-[3px] w-[3px] flex-none rounded-full bg-[#C4C4C8]" />
    <span className="min-w-0 flex-1 text-[14px] leading-relaxed text-[#3F3F46]">
      {children}
    </span>
  </li>
);

/* What the Copy button on this card yields.
 *
 * A card cannot be copied as markup, and the raw payload is useless to anyone.
 * What a bid manager actually wants to paste into an email is the decision, the
 * number behind it and the one line of reasoning — so that is what it produces,
 * built here where the payload's shape is understood rather than in the turn
 * component that only knows it holds "a card". */
export const qualifySummaryText = (message) => {
  const d = message?.data || {};
  const score = d["readiness score"];
  const v = verdictOf(d.verdict);
  const fd = message?.fitSummaryState === "ready" ? message.fitSummary || {} : null;

  const lines = [`Bid readiness — ${message?.projectLabel || "project"}`];
  if (fd?.what_it_is) lines.push("", fd.what_it_is, "");
  if (typeof score === "number") lines.push(`Score: ${score}/100`);
  lines.push(`Verdict: ${v.label}`);
  if (d.verdict_recommendation) lines.push("", String(d.verdict_recommendation).trim());

  if (fd) {
    (fd.reasons || []).forEach((r) => lines.push(`  - ${r}`));
    (fd.last_time || []).forEach((o) =>
      lines.push(`  - ${o.outcome}: ${o.project}${o.why ? ` (${o.why})` : ""}`)
    );
    if ((fd.youd_need || []).length) {
      lines.push("You'd need:");
      fd.youd_need.forEach((n) => lines.push(`  - ${n}`));
    }
  }
  return lines.join("\n");
};

/* Evidence bands over the backend's 0-5 quality score. 3 is its own band, not
 * folded into "good": the scorer treats >=3 as aligned, so 3 is the exact line
 * between counted and not — and a bid resting on a pile of 3s is a different
 * risk from one resting on 5s. */
const BANDS = [
  { key: "strong",  label: "Strong",   color: "#1F7D38", hit: (s) => s >= 4 },
  { key: "partial", label: "Adequate", color: "#84A63C", hit: (s) => s === 3 },
  { key: "weak",    label: "Weak",     color: "#92620B", hit: (s) => s >= 1 && s <= 2 },
  { key: "none",    label: "No evidence", color: "#DC2626", hit: (s) => !s || s <= 0 },
];

const relDay = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days < 1) return "today";
  if (days === 1) return "yesterday";
  return `${days} days ago`;
};

const pointColor = (earned, available) => {
  const r = available > 0 ? earned / available : 0;
  return r >= 0.7 ? "#1F7D38" : r >= 0.4 ? "#92620B" : "#DC2626";
};

/* Donut gauge — animates from empty after mount so the dial visibly fills. */
const Gauge = ({ score, color }) => {
  const R = 52;
  const C = 2 * Math.PI * R;
  const [drawn, setDrawn] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setDrawn(true));
    return () => cancelAnimationFrame(raf);
  }, []);
  return (
    <div className="relative h-[124px] w-[124px] flex-none">
      <svg aria-hidden="true" viewBox="0 0 120 120" className="h-full w-full -rotate-90">
        <circle cx="60" cy="60" r={R} fill="none" stroke="#18101C" strokeOpacity="0.06" strokeWidth="11" />
        <circle
          cx="60" cy="60" r={R} fill="none"
          stroke={color} strokeWidth="11" strokeLinecap="round"
          strokeDasharray={C}
          strokeDashoffset={drawn ? C * (1 - Math.min(score, 100) / 100) : C}
          className="transition-[stroke-dashoffset] duration-1000 ease-out motion-reduce:transition-none"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[29px] font-bold leading-none tracking-[-0.02em] text-[#18101C]">{score}</span>
        <span className="mt-0.5 text-[11px] font-medium uppercase tracking-[0.08em] text-[#77707E]">
          out of 100
        </span>
      </div>
    </div>
  );
};

/* One aspect, expressed in points rather than a bare percentage.
 *
 * The bar is filled to points EARNED against the width of points AVAILABLE, so
 * a wide bar means the aspect matters and a short fill means it is costing you.
 * Reading two aspects against each other now compares like with like. */
const AspectRow = ({ a, maxAvailable, onOpen }) => {
  const widthPct = maxAvailable > 0 ? (a.available / maxAvailable) * 100 : 0;
  const fillPct = a.available > 0 ? (a.earned / a.available) * 100 : 0;
  const color = pointColor(a.earned, a.available);
  /* The row is the handle. "6.8 of 19.1 points" states the problem and gives no
     way to act on it; the requirements underneath are the actionable part, and
     they were unreachable. */
  const Tag = onOpen ? "button" : "div";
  return (
    <Tag
      {...(onOpen
        ? {
            type: "button",
            onClick: () => onOpen(a.name),
            "data-sensa-keep-panel": true,
            title: `See the ${a.reqs} requirement${a.reqs === 1 ? "" : "s"} scored under ${a.name}`,
          }
        : {})}
      className={`w-full text-left ${
        onOpen
          ? "group/aspect -mx-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-[#18101C]/[0.035] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none"
          : ""
      }`}
    >
      <div className="mb-1 flex items-baseline justify-between gap-3">
        <span className="min-w-0 truncate text-[13.5px] font-medium text-[#3F3F46]">
          {a.name}
          <span className="ml-1.5 text-[11.5px] font-normal text-[#77707E]">
            {a.reqs} req{a.reqs === 1 ? "" : "s"}
            {a.thin && " · thin sample"}
          </span>
        </span>
        <span className="flex-none text-right text-[12.5px] font-semibold tabular-nums" style={{ color }}>
          {a.earned.toFixed(1)}
          <span className="font-normal text-[#77707E]"> / {a.available.toFixed(1)} pts</span>
          {/* What it is COSTING. Six rows of "earned / available" made the
              reader do six subtractions to find where the damage was. */}
          {a.lost >= 0.1 && (
            <span className="ml-1.5 font-normal text-[#77707E]">−{a.lost.toFixed(1)}</span>
          )}
        </span>
      </div>
      <div className="h-[6px] w-full rounded-full bg-[#18101C]/[0.04]">
        <div className="h-full rounded-full bg-[#18101C]/[0.07]" style={{ width: `${widthPct}%` }}>
          <div
            className="h-full rounded-full transition-[width] duration-700 ease-out motion-reduce:transition-none"
            style={{ width: `${fillPct}%`, backgroundColor: color }}
          />
        </div>
      </div>
      {/* Permanently visible. Hover-only affordances are ones most people never
          find — the same mistake the copy and thumbs buttons had. */}
      {onOpen && (
        <span className="mt-1 inline-flex items-center gap-1 text-[12px] font-medium text-[#A04A97]/80 transition-colors duration-150 group-hover/aspect:text-[#A04A97] motion-reduce:transition-none">
          See {a.reqs} requirement{a.reqs === 1 ? "" : "s"}
          <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h13M13 6l6 6-6 6" />
          </svg>
        </span>
      )}
    </Tag>
  );
};

/* One Go/No-Go check. */
const QaRow = ({ q }) => {
  const [open, setOpen] = useState(false);
  const answer = String(q.answer || "").trim();
  return (
    <li>
      <button
        type="button"
        onClick={() => q.found && answer && setOpen((v) => !v)}
        disabled={!q.found || !answer}
        aria-expanded={q.found && answer ? open : undefined}
        className={`flex w-full items-start gap-2.5 py-2.5 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none ${
          q.found && answer ? "hover:bg-[#18101C]/[0.025]" : "cursor-default"
        }`}
      >
        {q.found ? (
          <svg aria-hidden="true" className="mt-[3px] flex-none text-[#1F7D38]" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          <svg aria-hidden="true" className="mt-[3px] flex-none text-[#92620B]" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 8v4.5M12 16.2v.01" />
          </svg>
        )}
        <span className="min-w-0 flex-1">
          <span className="block text-[13.5px] font-medium leading-snug text-[#18101C]">
            {q.question}
          </span>
          {q.found ? (
            <span className={`mt-1 block text-[13px] leading-relaxed text-[#6B6472] ${open ? "" : "line-clamp-2"}`}>
              {answer}
            </span>
          ) : (
            <span className="mt-1 block text-[12.5px] font-semibold text-[#92620B]">
              Not stated in the RFP
            </span>
          )}
        </span>
      </button>
    </li>
  );
};

/* A ghost of the card being built.
 *
 * The steps say what is happening; this says what it is FOR. Two minutes of a
 * checklist with nothing else on screen gives the wait no destination — you are
 * watching a process rather than waiting for a thing. Seeing the shape arrive
 * first, then fill, is what turns it back into an object being made.
 *
 * Deliberately shapes, never numbers. A placeholder carrying a plausible score
 * would be read as the answer, and the answer is exactly what does not exist
 * yet. */
const GhostCard = () => (
  <div aria-hidden="true" className="mt-4 border-t border-[#18101C]/[0.06] pt-4">
    <div className="flex items-center gap-5">
      {/* A ring that breathes, not one that spins.
          A rotating shape parked in peripheral vision for minutes is genuinely
          tiring — the eye cannot stop tracking it — which is why the old
          spinner was taken out of this card in the first place. Reintroducing
          one here repeated that mistake. Pulsing matches the rest of the
          skeleton and stays still enough to ignore. */}
      <div className="relative h-[76px] w-[76px] flex-none">
        <span className="absolute inset-0 animate-pulse rounded-full border-[9px] border-[#18101C]/[0.06] motion-reduce:animate-none" />
      </div>
      <div className="min-w-0 flex-1 space-y-2">
        <div className="h-[22px] w-[104px] animate-pulse rounded-full bg-[#18101C]/[0.06] motion-reduce:animate-none" />
        <div className="h-[9px] w-full animate-pulse rounded-full bg-[#18101C]/[0.05] motion-reduce:animate-none" />
        <div className="h-[9px] w-4/5 animate-pulse rounded-full bg-[#18101C]/[0.05] motion-reduce:animate-none" />
      </div>
    </div>

    <div className="mt-4 space-y-2.5">
      {[100, 74, 88, 58].map((w, i) => (
        <div key={i} className="space-y-1.5">
          <div
            className="h-[8px] animate-pulse rounded-full bg-[#18101C]/[0.05] motion-reduce:animate-none"
            style={{ width: `${w * 0.42}%`, animationDelay: `${i * 140}ms` }}
          />
          <div
            className="h-[6px] animate-pulse rounded-full bg-[#18101C]/[0.04] motion-reduce:animate-none"
            style={{ width: `${w}%`, animationDelay: `${i * 140}ms` }}
          />
        </div>
      ))}
    </div>
  </div>
);

/* The pending state — no spinner over the steps themselves. The stage list
 * already carries every live signal; a large rotating shape parked in
 * peripheral vision for a multi-minute run adds nothing and is tiring, because
 * the eye keeps tracking it. */
const Pending = ({ stage, documents, requirements, scoringTotal, excludedCount }) => (
  <div className="max-w-[520px]">
    <CopilotQualifyProgress
      stage={stage}
      documents={documents}
      requirements={requirements}
      scoringTotal={scoringTotal}
      excludedCount={excludedCount}
    />
    <GhostCard />
  </div>
);

const CopilotQualifyCard = ({ message, onRerun, onOpenAspect, onOpenBand }) => {
  const { state, projectLabel, data, error } = message;
  const [allAspects, setAllAspects] = useState(false);
  const [qaOpen, setQaOpen] = useState(false);

  const model = useMemo(() => {
    if (!data) return null;
    const raw = data["capability and fit check"] || [];

    // Only aspects with requirements are scored — the backend normalises over
    // exactly these, so this is the denominator the score actually used.
    const scored = raw.filter((a) => (a.requirements_total || 0) > 0);
    const skipped = raw.filter((a) => !(a.requirements_total || 0));
    const totalWeight = scored.reduce((n, a) => n + (a.weight || 0), 0);

    const aspects = scored
      .map((a) => {
        const reqs = a.requirements_total || 0;
        const ratio = reqs > 0 ? (a.score_sum || 0) / (reqs * 5) : 0;
        const available = totalWeight > 0 ? ((a.weight || 0) / totalWeight) * 100 : 0;
        return {
          name: a.name,
          reqs,
          available,
          earned: ratio * available,
          lost: available - ratio * available,
          // Fewer than three requirements cannot represent an aspect; the score
          // is real but the sample behind it is not worth trusting alone.
          thin: reqs > 0 && reqs < 3,
        };
      })
      // Biggest loss first: the reason NOT to bid is what deserves the top of
      // the list, and a run of green tells you nothing the verdict didn't.
      .sort((x, y) => y.lost - x.lost);

    // Distribution over every scored requirement. Preferred from the live
    // payload; falls back to the summary persisted with a restored card.
    let dist = data.evidence_distribution || null;
    if (!dist) {
      const counts = { strong: 0, partial: 0, weak: 0, none: 0 };
      let seen = 0;
      raw.forEach((a) =>
        (a.requirements || []).forEach((r) => {
          const s = Number(r.confidence_score) || 0;
          const band = BANDS.find((b) => b.hit(s));
          if (band) counts[band.key] += 1;
          seen += 1;
        })
      );
      dist = seen > 0 ? counts : null;
    }

    /* The organisation's own Go/No-Go questions, answered from the RFP.
     * Unanswered first: "the RFP never states the bonding requirement" is a
     * job to do, while an answered question is just information. */
    const evalQa = (data.evaluation_qa || [])
      .filter((q) => q && q.question)
      .map((q) => ({
        question: q.question,
        answer: q.answer || "",
        found: !!q.found,
        sources: q.sources || [],
      }))
      .sort((a, b) => Number(a.found) - Number(b.found));

    // Sorted by points LOST, so the list reads top-down as a priority order and
    // the row that matters most is the one you see first.
    aspects.sort((x, y) => y.lost - x.lost);

    return {
      aspects,
      skipped,
      dist,
      evalQa,
      evalUnanswered: evalQa.filter((q) => !q.found).length,
      maxAvailable: Math.max(...aspects.map((a) => a.available), 1),
      opportunity: aspects[0],
      thinCount: aspects.filter((a) => a.thin).length,
    };
  }, [data]);

  const score = data?.["readiness score"] ?? 0;
  const v = verdictOf(data?.verdict);
  const distTotal = model?.dist
    ? BANDS.reduce((n, b) => n + (model.dist[b.key] || 0), 0)
    : 0;

  return (
    <div className="w-full max-w-[640px] rounded-2xl border border-[#18101C]/[0.08] bg-white p-5 shadow-[0_1px_2px_rgba(24,16,28,0.04),0_14px_34px_-24px_rgba(24,16,28,0.5)] animate-copilot-rise motion-reduce:animate-none">
      <div className="mb-4 flex items-center justify-between gap-3">
        <span className="text-[12px] font-semibold uppercase tracking-[0.07em] text-[#77707E]">
          Bid readiness · {projectLabel}
        </span>
        {state === "ready" && data?.analyzed_at && (
          <span className="flex-none text-[12px] text-[#77707E]">
            analysed {relDay(data.analyzed_at)}
          </span>
        )}
      </div>

      {state !== "ready" && state !== "error" && (
        <Pending
          stage={message.stage || "checking"}
          documents={message.documents}
          requirements={message.requirements}
          scoringTotal={message.scoringTotal}
          excludedCount={message.excludedCount}
        />
      )}

      {state === "error" && <p className="text-[14px] leading-relaxed text-[#B91C1C]">{error}</p>}

      {state === "ready" && data && model && (
        <>
          {/* What the RFP actually is, before any judgement of it: you cannot
              read a 39/100 until you know what was scored. From the fitment
              check (runFitSummary), not the point score below. */}
          {message.fitSummaryState === "ready" && message.fitSummary?.what_it_is && (
            <p className="mb-4 border-b border-[#18101C]/[0.06] pb-4 text-[13.5px] leading-relaxed text-[#3F3F46]">
              {message.fitSummary.what_it_is}
            </p>
          )}

          <div className="flex items-center gap-5">
            <Gauge score={score} color={v.color} />
            <div className="min-w-0 flex-1">
              <span
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[14.5px] font-bold"
                style={{ color: v.color, backgroundColor: v.soft, boxShadow: `inset 0 0 0 1px ${v.ring}` }}
              >
                {v.label}
              </span>
              <p className="mt-1.5 text-[13.5px] font-medium text-[#3F3F46]">{v.line}</p>
              <p className="mt-1.5 text-[13px] leading-relaxed text-[#77707E]">
                {data.verdict_recommendation}
              </p>
            </div>
          </div>

          {/* Why it does or doesn't fit. Placed right under the score's own
              verdict rather than in a separate labelled section: one project
              gets one verdict read, not a second one further down the card. */}
          {message.fitSummaryState === "ready" && !!message.fitSummary?.reasons?.length && (
            <ul className="mt-4 border-t border-[#18101C]/[0.06] pt-4">
              {message.fitSummary.reasons.map((r, i) => (
                <SummaryLine key={i}>{r}</SummaryLine>
              ))}
            </ul>
          )}

          {/* The organisation's Go/No-Go questions, answered from the RFP.
              Placed above the evidence arithmetic on purpose: "is there a
              mandatory bonding requirement" decides a bid outright, while an
              evidence percentage only shades it. */}
          {model.evalQa.length > 0 && (
            <div className="mt-5">
              <button
                type="button"
                onClick={() => setQaOpen((o) => !o)}
                className="flex w-full items-center justify-between gap-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97]"
              >
                <span className="text-[12px] font-semibold uppercase tracking-[0.07em] text-[#77707E]">
                  Go / No-Go checks
                </span>
                <span className="flex flex-none items-center gap-2">
                  <span className="text-[12px] text-[#6B6472]">
                    <span className="font-semibold text-[#1F7D38]">
                      {model.evalQa.length - model.evalUnanswered}
                    </span>
                    {" answered"}
                    {model.evalUnanswered > 0 && (
                      <>
                        {" · "}
                        <span className="font-semibold text-[#92620B]">{model.evalUnanswered}</span>
                        {" unclear"}
                      </>
                    )}
                  </span>
                  <svg aria-hidden="true"
                    width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#77707E"
                    strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"
                    className={`transition-transform duration-200 ${qaOpen ? "rotate-180" : ""} motion-reduce:transition-none`}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </span>
              </button>

              {/* A list, not a stack of tinted cards.
                *
                * Every check used to be its own filled box, so four questions
                * became four blocks of the same size and weight and the section
                * ran a screen deep. And "The RFP doesn't state this — check the
                * documents before deciding" was printed under EVERY unanswered
                * one: the same sentence, repeated, saying nothing the second
                * time. It is a two-word state now — the sentence it replaced is
                * advice, and advice repeated per row is noise.
                *
                * Answers clamp to two lines and open on click. They run three or
                * four lines each, and five expanded answers is not a summary. */}
              <ul className="mt-1 divide-y divide-[#18101C]/[0.06]">
                {(qaOpen ? model.evalQa : model.evalQa.filter((q) => !q.found)).map((q, i) => (
                  <QaRow key={i} q={q} />
                ))}
              </ul>

              {!qaOpen && model.evalUnanswered === 0 && (
                <p className="text-[12.5px] text-[#6B6472]">
                  All {model.evalQa.length} checks answered from the RFP.
                </p>
              )}
            </div>
          )}

          {/* Evidence quality across every requirement. The single most useful
              view in the payload and previously discarded entirely: a score of
              40 built on "mostly adequate" is a very different bid from the
              same 40 built on "half have nothing at all". */}
          {model.dist && distTotal > 0 && (
            <div className="mt-5">
              <p className="mb-1.5 text-[12px] font-semibold uppercase tracking-[0.07em] text-[#77707E]">
                Evidence across {distTotal} requirements
              </p>
              {/* The bar is the way in, not a picture of one.
                  "58 weak" is the most decision-useful number on this card and
                  the only question behind it is WHICH 58 — which spans areas, so
                  it cannot be answered by opening one. Both the segments and the
                  legend open the same filtered list. */}
              <div className="flex h-[9px] overflow-hidden rounded-full">
                {BANDS.map((b) => {
                  const n = model.dist[b.key] || 0;
                  if (!n) return null;
                  const seg = (
                    <div
                      style={{ width: "100%", backgroundColor: b.color, height: "100%" }}
                      className="transition-opacity duration-200 hover:opacity-80 motion-reduce:transition-none"
                    />
                  );
                  return onOpenBand ? (
                    <button
                      key={b.key}
                      type="button"
                      onClick={() => onOpenBand(b.key)}
                      data-sensa-keep-panel
                      title={`See the ${n} with ${b.label.toLowerCase()} evidence`}
                      aria-label={`See the ${n} requirements with ${b.label.toLowerCase()} evidence`}
                      style={{ width: `${(n / distTotal) * 100}%` }}
                      className="focus:outline-none focus-visible:ring-2 focus-visible:ring-[#18101C]"
                    >
                      {seg}
                    </button>
                  ) : (
                    <div key={b.key} title={`${b.label}: ${n}`} style={{ width: `${(n / distTotal) * 100}%`, backgroundColor: b.color }} />
                  );
                })}
              </div>
              <div className="mt-1.5 flex flex-wrap gap-x-1 gap-y-1">
                {BANDS.map((b) => {
                  const n = model.dist[b.key] || 0;
                  if (!n) return null;
                  const Tag = onOpenBand ? "button" : "span";
                  return (
                    <Tag
                      key={b.key}
                      {...(onOpenBand ? { type: "button", onClick: () => onOpenBand(b.key), "data-sensa-keep-panel": true } : {})}
                      className={`inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 text-[12px] text-[#6B6472] ${
                        onOpenBand
                          ? "transition-colors hover:bg-[#18101C]/[0.05] hover:text-[#18101C] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97] motion-reduce:transition-none"
                          : ""
                      }`}
                    >
                      <span className="h-2 w-2 flex-none rounded-full" style={{ backgroundColor: b.color }} />
                      <span className="font-semibold tabular-nums text-[#3F3F46]">{n}</span> {b.label.toLowerCase()}
                    </Tag>
                  );
                })}
              </div>
            </div>
          )}

          {/* Points, not percentages — see the file header. */}
          <div className="mt-5">
            <div className="mb-2 flex items-baseline justify-between">
              <p className="text-[12px] font-semibold uppercase tracking-[0.07em] text-[#77707E]">
                Where the score comes from
              </p>
              <span className="text-[11.5px] text-[#77707E]">points earned / available</span>
            </div>
            {/* Where these criteria came from.
             *
             * The areas below are no longer the same for every bid — they are
             * usually read out of the RFP itself, and sometimes they are the
             * organisation's chosen preset instead. A reader comparing two
             * projects needs to know which, because criteria derived from one
             * RFP are not the criteria the next one was judged on.
             *
             * Absent on analyses run before this existed, so it renders nothing
             * rather than guessing. */}
            {data.criteria_source && (
              <p className="mb-2.5 text-[11.5px] text-[#8B8492]">
                {data.criteria_source === "derived"
                  ? "Criteria derived from this RFP's requirements"
                  : data.criteria_source === "preset"
                  ? "Criteria from your organisation's chosen scoring preset"
                  : "Criteria from your organisation's scoring settings"}
              </p>
            )}
            <div className="space-y-3">
              {(allAspects ? model.aspects : model.aspects.slice(0, 4)).map((a) => (
                <AspectRow key={a.name} a={a} maxAvailable={model.maxAvailable} onOpen={onOpenAspect} />
              ))}
            </div>
            {model.aspects.length > 4 && (
              <button
                type="button"
                onClick={() => setAllAspects((o) => !o)}
                className="mt-2 rounded-md px-1.5 py-0.5 -ml-1.5 text-[12.5px] font-medium text-[#A04A97] transition-colors duration-200 hover:bg-[#A04A97]/[0.08] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97]"
              >
                {allAspects ? "Show fewer" : `Show all ${model.aspects.length} areas`}
              </button>
            )}
          </div>

          {/* The "N critical gaps" list used to sit here and is deliberately
           * gone. The backend caps it at critical_gaps[:10] and takes the first
           * ten in iteration order rather than the ten worst, so it read "10
           * critical gaps" on every project that had more than ten — 64 on one,
           * 52 on another — while naming an arbitrary subset of them. The
           * requirements panel replaces it properly: open any scoring area and
           * filter to No evidence or Weak to see all of them, worst first, with
           * the evidence attached. */}
          {/* Three findings, three different jobs — so three different shapes.
           *
           * They used to be three stacked tinted boxes of the same size, which
           * is what made this read as machine-written: a recommendation, a
           * caveat and a list all given identical weight, so nothing said which
           * one to act on. The list has since gone entirely — see below. Now the
           * caveats are footnotes. */}
          {model.opportunity && model.opportunity.lost >= 1 && (
            <div className="mt-4 flex items-baseline gap-3 border-t border-[#18101C]/[0.06] pt-4">
              {/* The number is the point. It was buried mid-sentence behind
                  "Strengthening the evidence here is worth up to". */}
              <span className="flex-none text-[21px] font-bold leading-none tabular-nums text-[#A04A97]">
                +{model.opportunity.lost.toFixed(1)}
              </span>
              <p className="min-w-0 text-[13.5px] leading-snug text-[#6B6472]">
                <span className="font-semibold text-[#18101C]">{model.opportunity.name}</span>
                {" "}has more headroom than any other area.
              </p>
            </div>
          )}

          {/* Caveats read as footnotes because that is what they are — they
              qualify the score, they are not findings of their own. Boxing them
              gave a hedge the same presence as the recommendation above it. */}
          {(model.skipped.length > 0 || model.thinCount > 0) && (
            <p className="mt-3 text-[12.5px] leading-relaxed text-[#77707E]">
              {model.thinCount > 0 && (
                <>
                  {model.thinCount} area{model.thinCount === 1 ? "" : "s"} scored on fewer than 3
                  requirements, so {model.thinCount === 1 ? "its" : "their"} score moves easily.
                </>
              )}
              {model.thinCount > 0 && model.skipped.length > 0 && " "}
              {model.skipped.length > 0 && (
                <>
                  {model.skipped.map((a) => a.name).join(", ")} had no matching requirements and{" "}
                  {model.skipped.length === 1 ? "is" : "are"} excluded.
                </>
              )}
            </p>
          )}

          <div className="mt-4 flex items-center justify-between border-t border-[#18101C]/[0.05] pt-3">
            <span className="text-[12.5px] text-[#77707E]">
              {data.total_requirements} requirements scored against your knowledge base
              {/* What the score LEAVES OUT.
                  The scorer covers some requirement categories far better than
                  others — on real projects Commercial requirements score at 0%
                  and Forms at around a fifth — so a card that reports only what
                  it scored reads as a complete picture when it is not. Stating
                  the shortfall costs one clause and stops the number being
                  trusted more than it deserves. */}
              {/* Prefer the number the backend actually set aside. It knows
                  WHY each one was excluded; the extracted-minus-scored
                  difference is only an inference from two totals. */}
              {data.excluded_count > 0 ? (
                <>
                  {" "}
                  <span className="text-[#92620B]">
                    · {data.excluded_count} set aside as administrative
                  </span>
                </>
              ) : (
                typeof data.extracted_requirements === "number" &&
                data.extracted_requirements > (data.total_requirements || 0) && (
                  <>
                    {" "}
                    <span className="text-[#92620B]">
                      · {data.extracted_requirements - data.total_requirements} not covered
                    </span>
                  </>
                )
              )}
            </span>
            <button
              type="button"
              onClick={onRerun}
              className="flex-none rounded-lg px-2.5 py-1 text-[12.5px] font-medium text-[#A04A97] transition-colors duration-200 hover:bg-[#A04A97]/[0.08] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#A04A97]"
            >
              Re-run analysis
            </button>
          </div>

          {/* Precedent and gaps from the fitment check — the verdict and the
              reasons behind it are already said, right under the score's own
              verdict above; repeating them here would be the same read
              twice. This block is only what THAT one didn't cover: a
              matching closed bid, a procurement restriction, or what you'd
              need to prove. Nothing to show for any of those → nothing
              renders, not an empty divider. */}
          {message.fitSummaryState === "loading" && (
            <div className="mt-4 flex items-center gap-2 border-t border-[#18101C]/[0.06] pt-4">
              <span className="h-[6px] w-[6px] animate-pulse rounded-full bg-[#C4C4C8]" />
              <span className="text-[13.5px] text-[#8A8A94]">
                Checking this against what your workspace knows…
              </span>
            </div>
          )}

          {message.fitSummaryState === "error" && (
            <p className="mt-4 border-t border-[#18101C]/[0.06] pt-4 text-[13.5px] text-[#B91C1C]">
              {message.fitSummaryError}
            </p>
          )}

          {message.fitSummaryState === "ready" &&
            (() => {
              const fd = message.fitSummary || {};
              if (!fd.restriction && !fd.last_time?.length && !fd.youd_need?.length) return null;
              return (
                <div className="mt-4 border-t border-[#18101C]/[0.06] pt-4">
                  {fd.restriction && (
                    <div className="mb-3.5 rounded-lg border border-[#D9A441]/25 bg-[#D9A441]/[0.06] px-3.5 py-3">
                      <p className="text-[14px] font-semibold leading-snug text-[#7A5209]">
                        {fd.restriction.headline}
                      </p>
                      {fd.restriction.detail && (
                        <p className="mt-0.5 text-[13px] leading-relaxed text-[#7A5209]/80">
                          {fd.restriction.detail}
                        </p>
                      )}
                    </div>
                  )}

                  <PrecedentBlock items={fd.last_time} />

                  {!!fd.youd_need?.length && (
                    <div className="mt-3.5 border-t border-black/[0.06] pt-3">
                      <p className="mb-1 text-[13px] font-medium text-[#52525B]">
                        You&apos;d need
                      </p>
                      <ul>
                        {fd.youd_need.map((n, i) => (
                          <SummaryLine key={`n-${i}`}>{n}</SummaryLine>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })()}
        </>
      )}
    </div>
  );
};

export default CopilotQualifyCard;
